import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, User, AlertCircle, Loader2, ArrowLeft, Check } from 'lucide-react';
import '../styles/auth.css';

const LoginPage = () => {
  const [mode, setMode] = useState('login'); // login, register, forgot, reset
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (mode === 'login') {
        await login(email, password);
        navigate('/admin');
      } else if (mode === 'register') {
        await register(email, password, name);
        await login(email, password);
        navigate('/admin');
      } else if (mode === 'forgot') {
        const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });
        const data = await response.json();
        if (response.ok) {
          setSuccess(`Reset token generated. In production, this would be emailed to you.`);
          if (data.reset_token) {
            setResetToken(data.reset_token);
            setMode('reset');
          }
        } else {
          setError(data.detail || 'Failed to process request');
        }
      } else if (mode === 'reset') {
        const response = await fetch(`${API_URL}/api/auth/reset-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: resetToken, new_password: newPassword })
        });
        const data = await response.json();
        if (response.ok) {
          setSuccess('Password reset successfully! You can now login.');
          setMode('login');
          setResetToken('');
          setNewPassword('');
        } else {
          setError(data.detail || 'Failed to reset password');
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'register': return 'Create Account';
      case 'forgot': return 'Forgot Password';
      case 'reset': return 'Reset Password';
      default: return 'Welcome Back';
    }
  };

  const getSubtitle = () => {
    switch (mode) {
      case 'register': return 'Set up your admin account';
      case 'forgot': return 'Enter your email to receive a reset link';
      case 'reset': return 'Enter your new password';
      default: return 'Sign in to access the admin panel';
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <Link to="/" className="auth-logo">
            <span className="logo-icon">V</span>
            <span className="logo-text">VoxIT Admin</span>
          </Link>
          <h1>{getTitle()}</h1>
          <p>{getSubtitle()}</p>
        </div>

        {error && (
          <div className="auth-error">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="auth-success">
            <Check size={18} />
            <span>{success}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          {/* Register: Name field */}
          {mode === 'register' && (
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <div className="input-wrapper">
                <User size={18} />
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  required
                />
              </div>
            </div>
          )}

          {/* Login, Register, Forgot: Email field */}
          {(mode === 'login' || mode === 'register' || mode === 'forgot') && (
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <div className="input-wrapper">
                <Mail size={18} />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@voxitconsulting.media"
                  required
                />
              </div>
            </div>
          )}

          {/* Login, Register: Password field */}
          {(mode === 'login' || mode === 'register') && (
            <div className="form-group">
              <div className="label-row">
                <label htmlFor="password">Password</label>
                {mode === 'login' && (
                  <button 
                    type="button" 
                    className="forgot-link"
                    onClick={() => {
                      setMode('forgot');
                      setError('');
                      setSuccess('');
                    }}
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="input-wrapper">
                <Lock size={18} />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
              </div>
            </div>
          )}

          {/* Reset: Token field */}
          {mode === 'reset' && (
            <>
              <div className="form-group">
                <label htmlFor="token">Reset Token</label>
                <div className="input-wrapper">
                  <Lock size={18} />
                  <input
                    id="token"
                    type="text"
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value)}
                    placeholder="Paste reset token here"
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <div className="input-wrapper">
                  <Lock size={18} />
                  <input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    minLength={6}
                  />
                </div>
              </div>
            </>
          )}

          <button type="submit" className="auth-submit" disabled={loading}>
            {loading ? (
              <><Loader2 size={18} className="spinner" /> Processing...</>
            ) : (
              mode === 'login' ? 'Sign In' :
              mode === 'register' ? 'Create Account' :
              mode === 'forgot' ? 'Send Reset Link' :
              'Reset Password'
            )}
          </button>
        </form>

        <div className="auth-footer">
          {(mode === 'forgot' || mode === 'reset') ? (
            <p>
              <button 
                type="button" 
                className="auth-switch"
                onClick={() => {
                  setMode('login');
                  setError('');
                  setSuccess('');
                }}
              >
                <ArrowLeft size={14} /> Back to login
              </button>
            </p>
          ) : (
            <p>
              {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
              <button 
                type="button" 
                className="auth-switch"
                onClick={() => {
                  setMode(mode === 'login' ? 'register' : 'login');
                  setError('');
                  setSuccess('');
                }}
              >
                {mode === 'login' ? 'Create one' : 'Sign in'}
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
